import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {
  IBuildingRoomSale,
  IGetBuildingRoom,
  ISetBuildingRoomValues,
  StagingService
} from "../../../new-information/services/staging.service";
import {catchError, finalize, forkJoin, map, of} from "rxjs";
import {CommonModule} from "@angular/common";
import {RoomTypeEnum} from "../../../shared/models/models";
import {MatDialog} from "@angular/material/dialog";
import {CreateCommercial2Component} from "../create-commercial2/create-commercial2.component";
import {CreateParkingDetailComponent} from "../create-parking-detail/create-parking-detail.component";
import {PlaceInfoDialogComponent} from "../place-info-dialog/place-info-dialog.component";
import {delay} from "rxjs/operators";
import {ToastrService} from "ngx-toastr";
import {NumberWithSpacesPipe} from "../../../shared/pipes/number-with-spaces.pipe";
import {ParkingTypeEnum} from "../create-parking/create-parking.component";
import {HttpErrorResponse} from "@angular/common/http";
import {RoundPipe} from "../../../shared/pipes/round.pipe";
import { MatTooltipModule } from "@angular/material/tooltip";

const parkingMap: Record<number, number> = {
  900: 0,
  100: -1,
  200: -2,
  300: -3,
  400: -4,
  500: -5,
}

@Component({
  selector: 'app-commercial-rooms',
  standalone: true,
  imports: [CommonModule, NumberWithSpacesPipe, RoundPipe, MatTooltipModule],
  templateUrl: './commercial-rooms.component.html',
  styleUrl: './commercial-rooms.component.scss'
})
export class CommercialRoomsComponent implements OnChanges {

  @Input() buildingID: number;
  @Input() buildingNumber: any;
  @Input() houseNumber: any;
  @Input() roomTypeID: RoomTypeEnum;
  @Input() reloadTrigger: number;
  @Input() isSalesMode: boolean = false;
  @Output() saleDialogEmit = new EventEmitter<{place: IPlace, floorId: number}>();

  loading = false
  groupedFloors: IGroupedFloor[] = [];
  maxPlacesCount = 0;
  placeCount = 0;
  mainData: IBuildingRoomSale[] = [];

  groupedFloorsParking1: IGroupedFloor[] = [];
  groupedFloorsParking2: IGroupedFloor[] = [];
  warningMessage = 'Заполните все обязательные поля помещения перед реализацией.';

  constructor(private stagingService: StagingService,
              private dialog: MatDialog,
              private toastr: ToastrService) {
  }

  private mergeSales(rooms: IGetBuildingRoom[], sales: IBuildingRoomSale[] | null | undefined): IBuildingRoomSale[] {
    if (!sales?.length) {
      return rooms.map((room) => ({...room}));
    }

    const salesById = new Map<number, IBuildingRoomSale>(sales.map((s) => [s.id, s]));
    return rooms.map((room) => {
      const sale = salesById.get(room.id);
      return sale ? ({...room, ...sale}) : ({...room});
    });
  }

  loadData() {
    this.loading = true;
    forkJoin({
      rooms: this.stagingService.getBuildingRooms(this.buildingID, this.roomTypeID),
      sales: this.stagingService.getBuildingRoomSales(this.buildingID).pipe(
        catchError(() => of([] as IBuildingRoomSale[]))
      )
    })
      .pipe(
        map(({rooms, sales}) => ({rooms: this.mergeSales(rooms, sales)})),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: ({rooms}) => {
          this.mainData = rooms;

          if (this.roomTypeID === RoomTypeEnum.Commercial) {
            const {groupedFloors, maxPlacesCount} = this.mapRoomsByFloor(rooms);
            this.groupedFloors = groupedFloors;
            this.maxPlacesCount = maxPlacesCount;
          }

          if (this.roomTypeID === RoomTypeEnum.Parking) {

            /*Наземный*/
            const parking1 = this.mapRoomsByFloor(rooms.filter(f => f.roomTypeItemID === ParkingTypeEnum.Ground));
            this.groupedFloorsParking1 = parking1.groupedFloors;
            const maxPlacesCountParking1 = parking1.maxPlacesCount;
            /*Подземный*/
            const parking2 = this.mapRoomsByFloor(rooms.filter(f => f.roomTypeItemID === ParkingTypeEnum.Underground));
            this.groupedFloorsParking2 = parking2.groupedFloors;
            const maxPlacesCountParking2 = parking2.maxPlacesCount;

            this.maxPlacesCount = Math.max(maxPlacesCountParking1, maxPlacesCountParking2);
          }

          this.placeCount = rooms.length;
        },
        error: (err) => {
          console.error('Ошибка при получении данных:', err);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const reloadTriggerChanged = changes['reloadTrigger']
      && changes['reloadTrigger'].previousValue !== changes['reloadTrigger'].currentValue;
    const salesModeChanged = changes['isSalesMode']
      && !changes['isSalesMode'].firstChange
      && changes['isSalesMode'].previousValue !== changes['isSalesMode'].currentValue;
    const buildingChanged = changes['buildingID']
      && !changes['buildingID'].firstChange
      && changes['buildingID'].previousValue !== changes['buildingID'].currentValue;
    const roomTypeChanged = changes['roomTypeID']
      && !changes['roomTypeID'].firstChange
      && changes['roomTypeID'].previousValue !== changes['roomTypeID'].currentValue;

    if (reloadTriggerChanged || salesModeChanged || buildingChanged || roomTypeChanged) {
      this.loadData();
    }
  }

  checkPlace(place: IPlace): boolean {
    return this.roomTypeID === RoomTypeEnum.Commercial ? (!place.entrance || !place.placeNumber || !place.area) : (!place.placeNumber || !place.area);
  }


  mapRoomsByFloor(rooms: IBuildingRoomSale[]): { groupedFloors: IGroupedFloor[]; maxPlacesCount: number } {
    const grouped: { [floorID: number]: IGroupedFloor } = {};

    for (const room of rooms) {
      const floor = room.floorID;

      if (!grouped[floor]) {
        grouped[floor] = {
          floorID: floor,
          floorNumber: Number.isFinite(parkingMap[floor]) ? parkingMap[floor] : floor,
          totalArea: 0,
          roomTypeItemID: room.roomTypeItemID,
          places: []
        };
      }

      grouped[floor].totalArea += room.area ?? 0;
      grouped[floor].places.push({
        id: room.id,
        placeNumber: room.placeNumber,
        roomTypeItemTitle: room.roomTypeItemTitle,
        roomTypeItemID: room.roomTypeItemID,
        entrance: room.entrance,
        area: room.area,
        dbFileID: room.dbFileID ?? null,
        fileName: room.fileName ?? null,
        saleDate: room.saleDate ?? null,
        saleAmount: room.saleAmount ?? null,
        priceAmount: room.priceAmount ?? null,
        extension: room.extension ?? null,
      });
    }

    const getSortKey = (floorID: number): number => {
      const mapped = parkingMap[floorID];
      return Number.isFinite(mapped) ? mapped : floorID;
    };

    const groupedFloors = Object.values(grouped)
      // Для паркинга покажет порядок 0, -1, -2... (а не -5..0)
      .sort((a, b) => getSortKey(b.floorID) - getSortKey(a.floorID));
    console.log(groupedFloors);

    const maxPlacesCount = groupedFloors.reduce((max, floor) =>
      Math.max(max, floor.places.length), 0);


    return {groupedFloors, maxPlacesCount};
  }

  openPlace(place: IPlace, floor: IGroupedFloor) {

    const object: ISetBuildingRoomValues = {
      buildingID: this.buildingID,
      buildingRoomID: place.id,
      roomTypeItemID: place.roomTypeItemID,
      roomTypeItemTitle: place.roomTypeItemTitle,
      entrance: place.entrance,
      floorID: floor.floorID,
      placeNumber: place.placeNumber,
      area: place.area
    }


    this.dialog.open(CreateCommercial2Component, {
      backdropClass: 'no-backdrop',
      autoFocus: false,
      data: {
        room: object,
      }
    }).afterClosed().subscribe(res => {
      if (res) {
        place.entrance = res.entrance;
        place.area = res.area;
        place.placeNumber = res.placeNumber;
        place.roomTypeItemID = res.roomTypeItemID;
        place.roomTypeItemTitle = res.roomTypeItemTitle;
        floor.totalArea = floor.places.reduce((total, place) => total + place.area, 0);
      }
    });
  }

  openParking(place: IPlace, floor: IGroupedFloor) {
    const object: ISetBuildingRoomValues = {
      buildingID: this.buildingID,
      buildingRoomID: place.id,
      roomTypeItemID: place.roomTypeItemID,
      roomTypeItemTitle: place.roomTypeItemTitle,
      entrance: place.entrance,
      floorID: floor.floorID,
      placeNumber: place.placeNumber,
      area: place.area
    }

    this.dialog.open(CreateParkingDetailComponent, {
      backdropClass: 'no-backdrop',
      autoFocus: false,
      data: {
        room: object,
      }
    }).afterClosed().subscribe(res => {
      if (res) {
        place.entrance = res.entrance;
        place.area = res.area;
        place.placeNumber = res.placeNumber;
        place.roomTypeItemID = res.roomTypeItemID;
        place.roomTypeItemTitle = res.roomTypeItemTitle;
        floor.totalArea = floor.places.reduce((total, place) => total + place.area, 0);
      }
    });
  }

  deleteLoading = false;

  deleteRoom(id: number, index: number, floor: IGroupedFloor) {
    if (!confirm('Вы точно хотите удалить это помещение?')) return;

    this.deleteLoading = true;
    this.stagingService.delBuildingRooms(id)
      .pipe(
        delay(500),
        finalize(() => this.deleteLoading = false))
      .subscribe({
        next: (_) => {
          floor.places.splice(index, 1);
          floor.totalArea = floor.places.reduce((total, place) => total + place.area, 0);
          if (floor.places.length === 0) {
            this.groupedFloors.splice(this.groupedFloors.indexOf(floor), 1);
          }
          this.placeCount--;
          if (this.placeCount === 0) {
            this.maxPlacesCount = 0;
          }
          this.toastr.success('Успешно удалено!');
        },
        error: (err: HttpErrorResponse) => {
          this.toastr.error(err.message)
        }
      });
  }

  openPlaceInfo(place: IPlace, floor: IGroupedFloor) {
    this.dialog.open(PlaceInfoDialogComponent, {
      backdropClass: 'no-backdrop',
      autoFocus: false,
      data: {
        place,
        floorNumber: floor.floorNumber,
      }
    });
  }

  openSaleDialog(place: IPlace, floorId: number) {
    if (this.checkPlace(place)) {
      this.toastr.warning(this.warningMessage);
      return;
    }
    this.saleDialogEmit.emit({place, floorId});
  }

  isSold(place: IPlace): boolean {
    return !!(place.saleDate && place.priceAmount);
  }

  protected readonly RoomTypeEnum = RoomTypeEnum;
}


interface IGroupedFloor {
  floorID: number;
  floorNumber: number;
  totalArea: number;
  roomTypeItemID: number;
  places: IPlace[];
}

export interface IPlace {
  id: number;
  placeNumber: number;
  roomTypeItemTitle: string;
  roomTypeItemID: number;
  entrance: string;
  area: number;
  dbFileID?: number | null;
  fileName?: string | null;
  saleDate?: string | null;
  saleAmount?: number | null;
  priceAmount?: number | null;
  extension?: string | null;
}
